import { RefreshCw, AlertTriangle } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { Card, CardHeader, CardTitle, CardDescription, CardBody, Button, Skeleton } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useChannelAnalytics, type Channel } from '@/api/fastchannel';
import { formatBytes } from '@/pages/services/_shared/analytics-format';

/**
 * Channel analytics (phase 1: live delivery metrics from the owning packager).
 * Audience numbers here are a server-side approximation (CCV from the playlist-
 * poll window); true audience + QoE arrives with player beacons (phase 2).
 */
export function AnalyticsTab({ channel }: { channel: Channel }) {
  const { data, isLoading, isError, refetch, isFetching } = useChannelAnalytics(channel.id);

  const total = data ? data.cache_hits + data.cache_misses : 0;

  return (
    <Section
      title="Analytics"
      description="Live delivery + resource metrics for this channel."
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card>
        <CardHeader>
          <CardTitle>Delivery</CardTitle>
          <CardDescription>
            From the packager serving this channel. Viewer count is a server-side
            estimate (active over the last {data?.viewer_window_sec ?? 30}s); request
            and egress totals are cumulative since the channel started on its pod.
          </CardDescription>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 48 }} />
              ))}
            </div>
          ) : isError ? (
            <Alert
              tone="warning"
              title="Could not load metrics"
              actions={<Button size="sm" variant="secondary" onClick={() => void refetch()}>Retry</Button>}
            >
              The packager metrics endpoint is unavailable. The channel may not be assigned to a live packager yet.
            </Alert>
          ) : !data ? (
            <EmptyState
              icon={<AlertTriangle size={28} />}
              title="No metrics available"
              description="The channel may not be assigned to a live packager yet."
              actions={<Button onClick={() => void refetch()}>Retry</Button>}
            />
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <StatGroup columns={4}>
                <Stat
                  label="Active viewers"
                  value={data.active_viewers.toLocaleString()}
                  hint={`~ last ${data.viewer_window_sec}s`}
                />
                <Stat label="Egress" value={formatBytes(data.bytes_served)} hint="since pod start" />
                <Stat label="Segment requests" value={data.segment_requests.toLocaleString()} />
                <Stat
                  label="Cache hit ratio"
                  value={`${(data.cache_hit_ratio * 100).toFixed(1)}%`}
                  hint={total ? `${data.cache_hits.toLocaleString()} / ${total.toLocaleString()}` : 'pod-wide'}
                />
                <Stat label="Segments produced" value={data.segments_produced.toLocaleString()} />
                <Stat
                  label="Last segment"
                  value={data.last_segment_age_sec < 0 ? '—' : `${data.last_segment_age_sec.toFixed(1)}s ago`}
                  hint={data.last_segment_age_sec > 30 ? 'production stalled?' : undefined}
                />
              </StatGroup>
              <div>
                <Button
                  variant="secondary"
                  startIcon={<RefreshCw size={15} />}
                  disabled={isFetching}
                  onClick={() => void refetch()}
                >
                  Refresh
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {data?.pod && (
        <Card>
          <CardHeader>
            <CardTitle>Pod resources</CardTitle>
            <CardDescription>
              The packager process serving this channel — how much it loads the pod.
            </CardDescription>
          </CardHeader>
          <CardBody>
            <StatGroup columns={4}>
              <Stat
                label="Avg CPU"
                value={
                  data.pod.uptime_seconds > 0 && data.pod.num_cpu > 0
                    ? `${((data.pod.cpu_seconds / (data.pod.uptime_seconds * data.pod.num_cpu)) * 100).toFixed(1)}%`
                    : '—'
                }
                hint={`${data.pod.num_cpu} vCPU`}
              />
              <Stat label="Memory (heap)" value={formatBytes(data.pod.heap_bytes)} />
              <Stat label="Goroutines" value={data.pod.goroutines.toLocaleString()} />
              <Stat label="Channels on pod" value={data.pod.channels_on_pod.toLocaleString()} />
              <Stat
                label="Transmux jobs"
                value={data.pod.transmux_jobs.toLocaleString()}
                hint={`${data.pod.transmux_seconds.toFixed(1)}s ffmpeg`}
              />
            </StatGroup>
          </CardBody>
        </Card>
      )}
      </div>
    </Section>
  );
}

export default AnalyticsTab;
